import React, { useEffect, useState, useRef, startTransition } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Edit2, Plus, Users, Hammer, Calendar as CalIcon, IndianRupee, MapPin, Activity, TrendingUp, Layout, ShieldCheck, ShieldX, ExternalLink, CheckCircle } from "lucide-react";
import useAutoRefresh from "../lib/useAutoRefresh";

const statusColor = {
  pending: "bg-[#F59E0B]/15 text-[#B45309]",
  accepted: "bg-[#1C4532]/15 text-[#1C4532]",
  on_the_way: "bg-[#D9734E]/15 text-[#C26240]",
  completed: "bg-[#22C55E]/15 text-[#15803D]",
  rejected: "bg-[#94A3B8]/15 text-[#475569]",
  cancelled: "bg-[#94A3B8]/15 text-[#475569]",
};

const CACHE_KEY = "hg_admin_cache";
const CACHE_TTL = 15000; // 15s

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL) return null;
    return parsed.data;
  } catch { return null; }
}

function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const initialLoad = useRef(true);
  const [stats, setStats] = useState(() => readCache()?.stats || {});
  const [users, setUsers] = useState(() => readCache()?.users || []);
  const [workers, setWorkers] = useState(() => readCache()?.workers || []);
  const [bookings, setBookings] = useState(() => readCache()?.bookings || []);
  const [services, setServices] = useState(() => readCache()?.services || []);
  const [pendingWorkers, setPendingWorkers] = useState(() => readCache()?.pendingWorkers || []);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: "", description: "", base_price: 0, category: "Home", icon: "wrench", image_url: "" });
  const [open, setOpen] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [approveDialog, setApproveDialog] = useState(null);
  const [rejectReasons, setRejectReasons] = useState([]);
  const [rejectCustom, setRejectCustom] = useState("");
  const [editWorkerDialog, setEditWorkerDialog] = useState(null);
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [workerForm, setWorkerForm] = useState({ name: "", email: "", password: "", phone: "", address: "", service_id: "", price: 0, verified: false });
  const [savingWorker, setSavingWorker] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedWorkers, setSelectedWorkers] = useState(new Set());
  const [bookingFilter, setBookingFilter] = useState("all");

  const rejectOptions = [
    { value: "profile_pic", label: "Profile picture not valid" },
    { value: "aadhar", label: "Aadhar not valid" },
    { value: "payment", label: "Payment not valid" },
    { value: "name", label: "Name not valid" },
    { value: "address", label: "Address not valid" },
    { value: "phone", label: "Phone not valid" },
    { value: "email", label: "Email not valid" },
    { value: "price", label: "Price not valid" },
    { value: "service", label: "Service not valid" },
    { value: "custom", label: "Custom reason" },
  ];

  const toggleRejectReason = (value) => {
    setRejectReasons(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  useEffect(() => {
    if (!user) return;
    loadAll().finally(() => { setLoading(false); initialLoad.current = false; });
    // eslint-disable-next-line
  }, [user]);

  const loadAll = async () => {
    const [s, u, w, b, sv, pw] = await Promise.all([
      api.get("/admin/stats").catch(() => ({ data: {} })),
      api.get("/admin/users").catch(() => ({ data: [] })),
      api.get("/admin/workers").catch(() => ({ data: [] })),
      api.get("/bookings").catch(() => ({ data: [] })),
      api.get("/services").catch(() => ({ data: [] })),
      api.get("/admin/pending-workers").catch(() => ({data: []})),
    ]);
    const data = { stats: s.data, users: u.data, workers: w.data, bookings: b.data, services: sv.data, pendingWorkers: pw.data || [] };
    setStats(data.stats); setUsers(data.users); setWorkers(data.workers); setBookings(data.bookings); setServices(data.services); setPendingWorkers(data.pendingWorkers);
    if (!initialLoad.current) writeCache(data);
  };

  // Auto-refresh admin data every 10s while tab is visible
  useAutoRefresh(() => { if (user?.role === "admin") loadAll(); }, [user?.id], 10000);

  const deleteUser = async (id) => {
    if (!window.confirm(t("admin.confirmDeleteUser"))) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const openEditWorker = (w) => {
    setWorkerForm({ name: w.name || "", email: w.email || "", password: "", phone: w.phone || "", address: w.address || "", service_id: w.service_id || "", price: w.price || 0 });
    setEditWorkerDialog(w.id);
  };

  const saveEditWorker = async () => {
    setSavingWorker(true);
    try {
      const body = { name: workerForm.name, phone: workerForm.phone, address: workerForm.address, service_id: workerForm.service_id || null, price: Number(workerForm.price) };
      if (workerForm.email) body.email = workerForm.email;
      if (workerForm.password) body.password = workerForm.password;
      await api.put(`/admin/workers/${editWorkerDialog}`, body);
      toast.success("Worker updated");
      setEditWorkerDialog(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.error || "Failed"); } finally { setSavingWorker(false); }
  };

  const createWorker = async () => {
    setSavingWorker(true);
    try {
      await api.post("/admin/workers", workerForm);
      toast.success("Worker created");
      setAddWorkerOpen(false);
      setWorkerForm({ name: "", email: "", password: "", phone: "", address: "", service_id: "", price: 0, verified: false });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.error || "Failed"); } finally { setSavingWorker(false); }
  };

  const verifyWorker = async (workerId, approved) => {
    try {
      if (approved) {
        await api.put(`/admin/verify-worker/${workerId}`);
      }
      toast.success(approved ? "Worker approved" : "Worker rejected");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.error || "Failed"); }
  };

  const rejectWithReason = async () => {
    const id = rejectDialog;
    if (!id) return;
    const selected = rejectReasons.filter(r => r !== "custom").map(r => rejectOptions.find(o => o.value === r)?.label).filter(Boolean);
    if (rejectReasons.includes("custom") && rejectCustom.trim()) {
      selected.push(rejectCustom.trim());
    }
    if (selected.length === 0) { toast.error("Select at least one reason"); return; }
    const reason = selected.join("; ");
    await api.put(`/admin/verify-worker/${id}/reject`, { reason });
    toast.success("Worker rejected");
    setRejectDialog(null);
    setRejectReasons([]);
    setRejectCustom("");
    loadAll();
  };

  const openServiceDialog = (svc = null) => {
    if (svc) {
      setEditingService(svc);
      setServiceForm({ ...svc });
    } else {
      setEditingService(null);
      setServiceForm({ name: "", description: "", base_price: 0, category: "Home", icon: "wrench", image_url: "" });
    }
    setOpen(true);
  };

  const saveService = async () => {
    try {
      const payload = { ...serviceForm, base_price: parseFloat(serviceForm.base_price) };
      if (editingService) {
        await api.put(`/services/${editingService.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/services", payload);
        toast.success("Created");
      }
      setOpen(false); loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteService = async (id) => {
    if (!window.confirm(t("admin.confirmDeleteService"))) return;
    await api.delete(`/services/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const updateBookingStatus = async (id, status) => {
    await api.put(`/bookings/${id}/status`, { status });
    toast.success("Updated");
    loadAll();
  };

  const toggleSelect = (set, id) => set(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const selectAll = (set, items) => startTransition(() => set(new Set(items.map(i => i.id))));

  const clearSelection = (set) => set(new Set());

  const bulkDeleteUsers = async (ids) => {
    if (!window.confirm(`Delete ${ids.size} user${ids.size > 1 ? "s" : ""}?`)) return;
    await api.post("/admin/users/bulk-delete", { ids: [...ids] });
    toast.success(`Deleted ${ids.size} user${ids.size > 1 ? "s" : ""}`);
    clearSelection(setSelectedUsers);
    loadAll();
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/admin/bookings/${id}`);
    toast.success("Booking deleted");
    loadAll();
  };

  const bulkDeleteBookings = async (ids) => {
    if (!window.confirm(`Delete ${ids.size} booking${ids.size > 1 ? "s" : ""}?`)) return;
    await api.post("/admin/bookings/bulk-delete", { ids: [...ids] });
    toast.success(`Deleted ${ids.size} booking${ids.size > 1 ? "s" : ""}`);
    clearSelection(setSelectedBookings);
    loadAll();
  };

  const bulkDeleteWorkers = async (ids) => {
    if (!window.confirm(`Delete ${ids.size} worker${ids.size > 1 ? "s" : ""}?`)) return;
    await api.post("/admin/users/bulk-delete", { ids: [...ids] });
    toast.success(`Deleted ${ids.size} worker${ids.size > 1 ? "s" : ""}`);
    clearSelection(setSelectedWorkers);
    loadAll();
  };

  if (!user) return null;

  const filteredBookings = bookingFilter === "all" ? bookings : bookings.filter(b => b.status === bookingFilter);

  const totalBookings = Math.max(stats.bookings || 0, 1);
  const completedRate = Math.round(((stats.completed || 0) / totalBookings) * 100);
  const opsBars = [
    { label: "Users", value: stats.users || 0, tone: "bg-[#1C4532]" },
    { label: "Workers", value: stats.workers || 0, tone: "bg-[#D9734E]" },
    { label: "Pending", value: stats.pending || 0, tone: "bg-[#F59E0B]" },
    { label: "Done", value: stats.completed || 0, tone: "bg-[#22C55E]" },
  ];
  const maxOps = Math.max(...opsBars.map((item) => item.value), 1);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col dashboard-bg">
        <Navbar />
        <main className="flex-1 max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 md:py-8 w-full">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin h-10 w-10 border-2 border-[#1C4532] border-t-transparent rounded-full" />
              <p className="text-sm text-[#646964]">Loading dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <><Seo title="Admin Dashboard" description="Manage users, workers, bookings, and site content on Universal Tree." noindex />
    <div className="min-h-screen flex flex-col dashboard-bg">
      <Navbar />
      <main className="flex-1 max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 md:py-8 w-full">
        <section className="mobile-card md:premium-surface p-5 md:p-8">
          <div className="flex flex-col gap-4 md:gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 hidden md:block">
              <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold">{t("admin.title")}</p>
              <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight mt-2">{t("admin.heading")}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#646964]">{t("admin.subtitle")}</p>
              <Link to="/admin/cms" className="inline-flex items-center gap-2 mt-4 rounded-lg md:rounded-sm bg-[#1C4532]/10 text-[#1C4532] hover:bg-[#1C4532] hover:text-white transition-all px-4 py-2.5 text-sm font-semibold">
                <Layout size={16} /> Edit Home Page
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 min-w-[200px] md:min-w-[240px]">
              <div className="rounded-xl md:rounded-sm bg-[#1C4532] p-4 text-white">
                <Activity size={18} />
                <p className="mt-3 font-display text-2xl md:text-3xl">{completedRate}%</p>
                <p className="text-xs text-white/70">{t("admin.completionRate")}</p>
              </div>
              <div className="rounded-xl md:rounded-sm bg-[#FFF3ED] p-4 text-[#D9734E]">
                <TrendingUp size={18} />
                <p className="mt-3 font-display text-2xl md:text-3xl">₹{(stats.revenue || 0).toLocaleString()}</p>
                <p className="text-xs text-[#D9734E]/75">{t("admin.revenue")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label={t("admin.stat.customers")} value={stats.users || 0} icon={<Users size={18} strokeWidth={1.5}/>}/>
          <StatCard label={t("admin.stat.workers")} value={stats.workers || 0} icon={<Hammer size={18} strokeWidth={1.5}/>}/>
          <StatCard label={t("admin.stat.bookings")} value={stats.bookings || 0} icon={<CalIcon size={18} strokeWidth={1.5}/>}/>
          <StatCard label={t("admin.stat.pending")} value={stats.pending || 0} icon={<CalIcon size={18} strokeWidth={1.5}/>}/>
          <StatCard label={t("admin.stat.completed")} value={stats.completed || 0} icon={<CalIcon size={18} strokeWidth={1.5}/>}/>
          <StatCard label="Revenue" value={`₹${(stats.revenue || 0).toLocaleString()}`} icon={<IndianRupee size={18} strokeWidth={1.5}/>}/>
        </div>

        <Link to="/admin/cms" className="md:hidden mt-4 flex items-center justify-center gap-2 rounded-lg bg-[#1C4532]/10 text-[#1C4532] hover:bg-[#1C4532] hover:text-white transition-all px-4 py-3 text-sm font-semibold">
          <Layout size={16} /> Edit Home Page
        </Link>

        <section className="mt-4 md:mt-6 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
          <div className="mobile-card md:premium-surface p-5 md:p-6 lg:col-span-8">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#D9734E] font-bold">{t("admin.activity.label")}</p>
                <h2 className="mt-1 md:mt-2 font-display text-lg md:text-2xl text-[#1A1D1A]">{t("admin.activity.title")}</h2>
              </div>
              <Badge className="md:rounded-sm rounded-full bg-[#1C4532]/10 text-[#1C4532] shrink-0 ml-3">{t("admin.autoRefresh")}</Badge>
            </div>
            <div className="mt-6 md:mt-8 flex h-48 md:h-56 items-end gap-3 md:gap-4">
              {opsBars.map((item, i) => (
                <div key={item.label} className="flex min-w-0 flex-1 flex-col items-center gap-2 md:gap-3">
                  <div className="flex h-28 md:h-40 w-full items-end rounded-lg md:rounded-sm bg-[#F3F1ED] p-2">
                    <div
                      className={`bar-rise w-full rounded-lg md:rounded-sm ${item.tone} shadow-[0_14px_28px_rgba(28,69,50,0.12)]`}
                      style={{ height: `${Math.max((item.value / maxOps) * 100, 12)}%`, animationDelay: `${i * 90}ms` }}
                    />
                  </div>
                  <div className="text-center">
                    <p className="font-display text-xl md:text-2xl">{item.value}</p>
                    <p className="text-[10px] md:text-xs uppercase tracking-wider text-[#646964]">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mobile-card md:premium-surface relative p-5 md:p-6 lg:col-span-4">
            <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#D9734E] font-bold">{t("admin.bookingsHealth")}</p>
            <div className="mt-4 md:mt-6 grid place-items-center">
              <svg viewBox="0 0 120 120" className="h-36 w-36 md:h-44 md:w-44 -rotate-90">
                <circle cx="60" cy="60" r="44" fill="none" stroke="#F3F1ED" strokeWidth="14" />
                <circle
                  cx="60"
                  cy="60"
                  r="44"
                  fill="none"
                  stroke="#1C4532"
                  strokeWidth="14"
                  strokeLinecap="round"
                  strokeDasharray="276"
                  style={{ "--ring-offset": 276 - (276 * completedRate) / 100 }}
                  className="ring-draw"
                />
              </svg>
              <div className="absolute text-center">
                <p className="font-display text-3xl md:text-4xl text-[#1A1D1A]">{completedRate}%</p>
                <p className="text-[10px] md:text-xs uppercase tracking-wider text-[#646964]">{t("admin.complete")}</p>
              </div>
            </div>
          </div>
        </section>

        <Tabs defaultValue="bookings" className="mt-6 md:mt-10">
          <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide">
            <TabsList className="bg-[#F3F1ED] md:rounded-sm rounded-lg p-1 h-auto md:h-12 w-full sm:w-auto inline-flex gap-1 flex-wrap md:flex-nowrap">
              {[
                { value: "bookings", label: t("admin.tab.bookings"), testId: "admin-tab-bookings" },
                { value: "users", label: t("admin.tab.users"), testId: "admin-tab-users" },
                { value: "workers", label: t("admin.tab.workers"), testId: "admin-tab-workers" },
                { value: "services", label: t("admin.tab.services"), testId: "admin-tab-services" },
                { value: "map", label: t("admin.tab.map"), testId: "admin-tab-map" },
                { value: "verifications", label: "Verifications", testId: undefined, badge: pendingWorkers.length > 0 ? pendingWorkers.length : null },
              ].map(tab => (
                <TabsTrigger key={tab.value} value={tab.value} data-testid={tab.testId} className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white data-[state=active]:shadow-sm px-4 py-2.5 md:px-5 whitespace-nowrap text-xs md:text-sm font-semibold transition-all">
                  {tab.label}
                  {tab.badge != null && <span className="ml-1.5 bg-[#D9734E] text-white text-[10px] rounded-full px-1.5 py-0.5">{tab.badge}</span>}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="bookings" className="mt-5 md:mt-6">
            <div className="flex flex-wrap items-center gap-2">
              {["all","pending","accepted","completed","cancelled"].map(s => (
                <button key={s} onClick={() => { setBookingFilter(s); clearSelection(setSelectedBookings); }} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${bookingFilter === s ? "bg-[#1C4532] text-white" : "bg-[#F3F1ED] text-[#646964] hover:bg-[#E2E0D9]"}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
              ))}
              <div className="ml-auto flex gap-1">
                <Button size="sm" onClick={() => { if (window.confirm("Cancel all pending bookings?")) { clearSelection(setSelectedBookings); setLoading(true); api.post("/admin/bookings/bulk-delete", { ids: bookings.filter(b => b.status === "pending").map(b => b.id) }).then(() => { toast.success("Pending bookings cancelled"); loadAll(); }).catch(() => toast.error("Failed")).finally(() => setLoading(false)); } }} variant="outline" className="rounded-lg text-xs border-red-300 text-red-600">Cancel All Pending</Button>
                <Button size="sm" onClick={() => { if (window.confirm("Delete all cancelled bookings?")) { clearSelection(setSelectedBookings); setLoading(true); api.post("/admin/bookings/bulk-delete", { ids: bookings.filter(b => b.status === "cancelled").map(b => b.id) }).then(() => { toast.success("Cancelled bookings deleted"); loadAll(); }).catch(() => toast.error("Failed")).finally(() => setLoading(false)); } }} variant="outline" className="rounded-lg text-xs border-red-300 text-red-600">Del All Cancelled</Button>
              </div>
            </div>
            {selectedBookings.size > 0 && (
              <div className="mb-3 flex items-center gap-3 px-3 pb-3 pt-0 bg-[#1C4532]/5 rounded-lg">
                <span className="text-sm font-medium text-[#1A1D1A]">{selectedBookings.size} selected</span>
                <Button size="sm" onClick={() => bulkDeleteBookings(selectedBookings)} className="rounded-lg bg-red-600 text-white text-xs">Cancel Selected</Button>
                <Button size="sm" variant="outline" onClick={() => clearSelection(setSelectedBookings)} className="rounded-lg text-xs">Clear</Button>
              </div>
            )}
            <div className="mobile-card md:premium-surface overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F1ED]">
                    <tr>
                      <th className="text-left px-3 md:px-4 py-3 w-12">
                        <input type="checkbox" checked={filteredBookings.length > 0 && selectedBookings.size === filteredBookings.length} onChange={e => e.target.checked ? selectAll(setSelectedBookings, filteredBookings) : clearSelection(setSelectedBookings)} className="accent-[#1C4532] scale-125 md:scale-100" />
                      </th>
                      {[t("admin.table.service"), t("admin.table.customer"), t("admin.table.worker"), t("admin.table.amount"), t("admin.table.status"), t("admin.table.created"), t("admin.table.actions")].map(c => <th key={c} className="text-left px-3 md:px-4 py-3 text-[10px] md:text-xs uppercase tracking-wider text-[#646964] font-bold">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map(b => (
                      <tr key={b.id} className="border-t border-[#E2E0D9]">
                        <td className="px-3 md:px-4 py-3">
                          <input type="checkbox" checked={selectedBookings.has(b.id)} onChange={() => toggleSelect(setSelectedBookings, b.id)} className="accent-[#1C4532] scale-125 md:scale-100" />
                        </td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{b.service_name}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{b.full_name}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{b.worker_id ? (workers.find(w => w.id === b.worker_id)?.name || b.worker_id) : "—"}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">₹{b.amount}</td>
                        <td className="px-3 md:px-4 py-3"><Badge className={`${statusColor[b.status]} md:rounded-sm rounded-full text-xs`}>{b.status.replace(/_/g, " ")}</Badge></td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{new Date(b.created_at).toLocaleDateString()}</td>
                        <td className="px-3 md:px-4 py-3">
                          <div className="flex gap-1">
                            {b.status !== "completed" && b.status !== "cancelled" && (
                              <Button size="sm" variant="outline" onClick={() => updateBookingStatus(b.id, "completed")} className="text-xs h-8 md:h-7 rounded-lg md:rounded-sm">{t("admin.markDone")}</Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => deleteBooking(b.id)} className="text-[#D9734E] h-8 md:h-7"><Trash2 size={14}/></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredBookings.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-[#646964] text-sm">{t("admin.noRecords")}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-5 md:mt-6">
            {selectedUsers.size > 0 && (
              <div className="mb-3 flex items-center gap-3 p-3 bg-[#1C4532]/5 rounded-lg">
                <span className="text-sm font-medium text-[#1A1D1A]">{selectedUsers.size} selected</span>
                <Button size="sm" onClick={() => bulkDeleteUsers(selectedUsers)} className="rounded-lg bg-red-600 text-white text-xs">Delete Selected</Button>
                <Button size="sm" variant="outline" onClick={() => clearSelection(setSelectedUsers)} className="rounded-lg text-xs">Clear</Button>
              </div>
            )}
            <div className="mobile-card md:premium-surface overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F1ED]">
                    <tr>
                      <th className="text-left px-3 md:px-4 py-3 w-12">
                        <input type="checkbox" checked={selectedUsers.size === users.length && users.length > 0} onChange={e => e.target.checked ? selectAll(setSelectedUsers, users) : clearSelection(setSelectedUsers)} className="accent-[#1C4532] scale-125 md:scale-100" />
                      </th>
                      {[t("admin.table.name"), t("admin.table.email"), t("admin.table.phone"), t("admin.table.address"), t("admin.table.joined"), ""].map(c => <th key={c} className="text-left px-3 md:px-4 py-3 text-[10px] md:text-xs uppercase tracking-wider text-[#646964] font-bold">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-t border-[#E2E0D9] hover:bg-[#FAF9F6] transition-none">
                        <td className="px-3 md:px-4 py-3">
                          <input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleSelect(setSelectedUsers, u.id)} className="accent-[#1C4532] scale-125 md:scale-100" />
                        </td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{u.name}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm font-mono">{u.email}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{u.phone || "—"}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{u.address || "—"}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="px-3 md:px-4 py-3">
                          <Button size="sm" variant="ghost" data-testid={`del-user-${u.id}`} onClick={() => deleteUser(u.id)} className="text-[#D9734E] hover:bg-[#D9734E]/10 h-8 md:h-7"><Trash2 size={14}/></Button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-[#646964] text-sm">{t("admin.noRecords")}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="workers" className="mt-5 md:mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                {selectedWorkers.size > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-[#1C4532]/5 rounded-lg">
                    <span className="text-sm font-medium text-[#1A1D1A]">{selectedWorkers.size} selected</span>
                    <Button size="sm" onClick={() => bulkDeleteWorkers(selectedWorkers)} className="rounded-lg bg-red-600 text-white text-xs">Delete Selected</Button>
                    <Button size="sm" variant="outline" onClick={() => clearSelection(setSelectedWorkers)} className="rounded-lg text-xs">Clear</Button>
                  </div>
                )}
              </div>
              <Button onClick={() => { setWorkerForm({ name: "", email: "", password: "", phone: "", address: "", service_id: "", price: 0 }); setAddWorkerOpen(true); }} className="rounded-lg bg-[#1C4532] hover:bg-[#143324] text-white text-sm shrink-0"><Plus size={14} className="mr-1" /> Add Worker</Button>
            </div>
            <div className="mobile-card md:premium-surface overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F1ED]">
                    <tr>
                      <th className="text-left px-3 md:px-4 py-3 w-12">
                        <input type="checkbox" checked={selectedWorkers.size === workers.length && workers.length > 0} onChange={e => e.target.checked ? selectAll(setSelectedWorkers, workers) : clearSelection(setSelectedWorkers)} className="accent-[#1C4532] scale-125 md:scale-100" />
                      </th>
                      {[t("admin.table.name"), "Service", t("admin.workerTable.price"), t("admin.workerTable.online"), "Verified", t("admin.workerTable.jobsDone"), "Phone", ""].map(c => <th key={c} className="text-left px-3 md:px-4 py-3 text-[10px] md:text-xs uppercase tracking-wider text-[#646964] font-bold">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map(w => (
                      <tr key={w.id} className="border-t border-[#E2E0D9] hover:bg-[#FAF9F6] transition-none">
                        <td className="px-3 md:px-4 py-3">
                          <input type="checkbox" checked={selectedWorkers.has(w.id)} onChange={() => toggleSelect(setSelectedWorkers, w.id)} className="accent-[#1C4532] scale-125 md:scale-100" />
                        </td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{w.name}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{services.find(s => s.id === w.service_id)?.name || "—"}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">₹{w.price || 0}</td>
                        <td className="px-3 md:px-4 py-3"><span className={`text-xs font-medium ${w.is_online ? "text-[#22C55E]" : "text-[#94A3B8]"}`}>{w.is_online ? `● ${t("common.online")}` : `○ ${t("common.offline")}`}</span></td>
                        <td className="px-3 md:px-4 py-3"><span className={`text-xs font-medium ${w.verified ? "text-[#22C55E]" : "text-[#D9734E]"}`}>{w.verified ? "Verified" : "Pending"}</span></td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{w.jobs_completed || 0}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{w.phone || "—"}</td>
                        <td className="px-3 md:px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditWorker(w)} className="h-8 md:h-7"><Edit2 size={14}/></Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteUser(w.id)} className="text-[#D9734E] hover:bg-[#D9734E]/10 h-8 md:h-7"><Trash2 size={14}/></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {workers.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-[#646964] text-sm">{t("admin.noRecords")}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-5 md:mt-6">
            <Button onClick={() => openServiceDialog(null)} data-testid="add-service" className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white mb-4 w-full md:w-auto"><Plus size={14} className="mr-2"/>{t("admin.addService")}</Button>
            <div className="mobile-card md:premium-surface overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F3F1ED]">
                    <tr>{[t("admin.table.name"), t("admin.serviceTable.category"), t("admin.serviceTable.basePrice"), t("admin.serviceTable.description"), t("admin.table.actions")].map(c => <th key={c} className="text-left px-3 md:px-4 py-3 text-[10px] md:text-xs uppercase tracking-wider text-[#646964] font-bold">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {services.map(s => (
                      <tr key={s.id} className="border-t border-[#E2E0D9] hover:bg-[#FAF9F6]">
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{s.name}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">{s.category}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm">₹{s.base_price}</td>
                        <td className="px-3 md:px-4 py-3 text-xs md:text-sm max-w-[120px] md:max-w-none truncate">{s.description}</td>
                        <td className="px-3 md:px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openServiceDialog(s)} className="h-8 md:h-7"><Edit2 size={14}/></Button>
                            <Button size="sm" variant="ghost" data-testid={`del-service-${s.id}`} onClick={() => deleteService(s.id)} className="text-[#D9734E] hover:bg-[#D9734E]/10 h-8 md:h-7"><Trash2 size={14}/></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {services.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-[#646964] text-sm">{t("admin.noRecords")}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

           <TabsContent value="map" className="mt-5 md:mt-6">
            {(() => {
              const located = workers.filter(w => w.lat != null && w.lng != null).map(w => ({ ...w, lat: Number(w.lat), lng: Number(w.lng) }));
              if (located.length === 0) return <p className="text-sm text-[#646964] py-12 text-center">{t("admin.noWorkersLocation")}</p>;
              const onlineCount = located.filter(w => w.is_online).length;
              const center = [located[0].lat, located[0].lng];
              return (
                <div className="grid grid-cols-12 gap-4 lg:gap-6">
                  <div className="col-span-12 lg:col-span-8">
                    <div className="flex flex-wrap items-center gap-3 mb-4 text-[10px] md:text-xs uppercase tracking-wider">
                      <span className="flex items-center gap-2 text-[#646964]"><span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]"></span>{t("admin.mapLegend.online")} ({onlineCount})</span>
                      <span className="flex items-center gap-2 text-[#646964]"><span className="w-2.5 h-2.5 rounded-full bg-[#94A3B8]"></span>{t("admin.mapLegend.offline")} ({located.length - onlineCount})</span>
                      <span className="text-[#646964]">{workers.length - located.length} {t("admin.mapLegend.noLocation")}</span>
                    </div>
                    <div className="rounded-xl md:rounded-sm overflow-hidden">
                      <MapView center={center} workers={located} height={560} zoom={11}/>
                    </div>
                  </div>
                  <div className="col-span-12 lg:col-span-4 space-y-2 max-h-[400px] md:max-h-[560px] overflow-y-auto pr-1">
                    {located.map(w => (
                      <div key={w.id} data-testid={`admin-worker-${w.id}`} className="mobile-card haptic-tap p-4">
                        <div className="flex justify-between items-start gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-display text-sm md:text-base text-[#1A1D1A] truncate">{w.name}</p>
                            <p className="text-xs text-[#646964] mt-0.5 truncate">{services.find(s => s.id === w.service_id)?.name || "—"}</p>
                          </div>
                          <span className={`text-xs font-medium shrink-0 ${w.is_online ? "text-[#22C55E]" : "text-[#94A3B8]"}`}>
                            {w.is_online ? `● ${t("common.online")}` : `○ ${t("common.offline")}`}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-[#646964] flex flex-wrap items-center gap-3">
                          <span className="flex items-center gap-1"><MapPin size={11}/>{w.lat.toFixed(3)}, {w.lng.toFixed(3)}</span>
                          <span>₹{w.price || 0}</span>
                          <span>⭐ {w.rating?.toFixed?.(1) || "5.0"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="verifications" className="mt-5 md:mt-6">
            <div className="space-y-4">
              {pendingWorkers.map(pw => (
                <div key={pw.worker_id} className="mobile-card md:premium-surface p-5">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      {pw.worker?.profile_pic ? (
                        <img src={pw.worker.profile_pic} alt="" className="w-14 h-14 rounded-full object-cover shrink-0 border border-[#E2E0D9]" onError={e => { e.target.style.display = "none" }} />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-[#F3F1ED] flex items-center justify-center shrink-0">
                          <Users size={20} className="text-[#646964]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-display text-lg font-medium">{pw.worker?.name || "Unknown"}</h3>
                          {pw.worker?.service_id && <span className="text-xs bg-[#F3F1ED] px-2 py-0.5 rounded-sm">{services.find(s => s.id === pw.worker.service_id)?.name || "—"}</span>}
                        </div>
                        <p className="text-xs text-[#646964]">{pw.worker?.email} · {pw.worker?.phone || "—"}</p>
                        {pw.worker?.address && <p className="text-xs text-[#646964] mt-0.5">{pw.worker.address}</p>}
                        <p className="text-xs text-[#646964] mt-1">Submitted {pw.submitted_at ? new Date(pw.submitted_at).toLocaleString() : "—"}</p>
                        <div className="flex flex-wrap gap-3 mt-3">
                          {pw.aadhar_url && (
                            <a href={pw.aadhar_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#1C4532] font-medium hover:underline">
                              <ExternalLink size={12} /> View Aadhar
                            </a>
                          )}
                          {pw.pan_url && (
                            <a href={pw.pan_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#1C4532] font-medium hover:underline">
                              <ExternalLink size={12} /> View PAN
                            </a>
                          )}
                          {pw.payment_screenshot_url && (
                            <a href={pw.payment_screenshot_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#1C4532] font-medium hover:underline">
                              <ExternalLink size={12} /> View Payment
                            </a>
                          )}
                          {pw.payment_status === "success" && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#15803D] bg-[#22C55E]/10 px-2 py-0.5 rounded-sm">
                              <CheckCircle size={12} /> Paid
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button onClick={() => setApproveDialog(pw.worker_id)} className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white">
                        <ShieldCheck size={14} className="mr-1" /> Approve
                      </Button>
                      <Button onClick={() => { setRejectDialog(pw.worker_id); setRejectReasons([]); setRejectCustom(""); }} variant="outline" className="rounded-sm border-[#D9734E] text-[#D9734E] hover:bg-[#D9734E]/10">
                        <ShieldX size={14} className="mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {pendingWorkers.length === 0 && (
                <div className="mobile-card md:premium-surface p-8 text-center">
                  <ShieldCheck size={32} className="mx-auto mb-3 text-[#22C55E] opacity-50" />
                  <p className="text-sm text-[#646964]">No pending verifications</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!rejectDialog} onOpenChange={o => { if (!o) setRejectDialog(null); }}>
          <DialogContent className="md:rounded-sm rounded-xl">
            <DialogHeader><DialogTitle className="font-display">Reject Worker</DialogTitle></DialogHeader>
            <p className="text-xs text-[#646964] mt-1">Select one or more reasons:</p>
            <div className="space-y-1 mt-2">
              {rejectOptions.map(r => r.value !== "custom" && (
                <label key={r.value} className="flex items-center gap-3 p-3 rounded-sm hover:bg-[#F3F1ED] cursor-pointer transition-colors">
                  <input type="checkbox" checked={rejectReasons.includes(r.value)} onChange={() => toggleRejectReason(r.value)} className="accent-[#1C4532]" />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
              <label className="flex items-center gap-3 p-3 rounded-sm hover:bg-[#F3F1ED] cursor-pointer transition-colors">
                <input type="checkbox" checked={rejectReasons.includes("custom")} onChange={() => toggleRejectReason("custom")} className="accent-[#1C4532]" />
                <span className="text-sm">Custom reason</span>
              </label>
              {rejectReasons.includes("custom") && (
                <div className="pl-10 pr-3 pb-2">
                  <Input placeholder="Enter reason..." value={rejectCustom} onChange={e => setRejectCustom(e.target.value)} className="rounded-sm" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={rejectWithReason} className="rounded-sm bg-[#D9734E] hover:bg-[#C26240] text-white w-full md:w-auto">Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!approveDialog} onOpenChange={o => { if (!o) setApproveDialog(null); }}>
          <DialogContent className="md:rounded-sm rounded-xl max-w-sm">
            <DialogHeader><DialogTitle className="font-display">Confirm Approval</DialogTitle></DialogHeader>
            <p className="text-sm text-[#646964] mt-2">Approve this worker? They will immediately get access to accept bookings.</p>
            {approveDialog && (() => {
              const pw = pendingWorkers.find(w => w.worker_id === approveDialog);
              return pw ? (
                <div className="mt-3 bg-[#F8F7F4] rounded-lg p-3 space-y-1">
                  <p className="text-sm font-semibold text-[#1A1D1A]">{pw.worker?.name || "Worker"}</p>
                  {pw.worker?.service_name && <p className="text-xs text-[#646964]">{pw.worker.service_name}</p>}
                </div>
              ) : null;
            })()}
            <DialogFooter className="mt-4">
              <Button onClick={() => setApproveDialog(null)} variant="outline" className="rounded-lg md:rounded-sm">Cancel</Button>
              <Button onClick={() => { verifyWorker(approveDialog, true); setApproveDialog(null); }} className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white">Confirm Approve</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editWorkerDialog} onOpenChange={o => { if (!o) setEditWorkerDialog(null); }}>
          <DialogContent className="md:rounded-sm rounded-xl">
            <DialogHeader><DialogTitle className="font-display">Edit Worker</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <Input placeholder="Name" value={workerForm.name} onChange={e => setWorkerForm(p => ({...p, name: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Email" value={workerForm.email} onChange={e => setWorkerForm(p => ({...p, email: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Password (leave blank to keep)" type="password" value={workerForm.password} onChange={e => setWorkerForm(p => ({...p, password: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Phone" value={workerForm.phone} onChange={e => setWorkerForm(p => ({...p, phone: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Address" value={workerForm.address} onChange={e => setWorkerForm(p => ({...p, address: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <select value={workerForm.service_id} onChange={e => setWorkerForm(p => ({...p, service_id: e.target.value}))} className="w-full rounded-lg md:rounded-sm border border-[#E2E0D9] bg-white h-12 md:h-10 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20">
                <option value="">Select service</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input type="number" placeholder="Price" value={workerForm.price} onChange={e => setWorkerForm(p => ({...p, price: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
            </div>
            <DialogFooter>
              <Button onClick={saveEditWorker} disabled={savingWorker} className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white w-full md:w-auto">{savingWorker ? "Saving..." : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={addWorkerOpen} onOpenChange={o => { if (!o) setAddWorkerOpen(false); }}>
          <DialogContent className="md:rounded-sm rounded-xl">
            <DialogHeader><DialogTitle className="font-display">Add Worker</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <Input placeholder="Name *" value={workerForm.name} onChange={e => setWorkerForm(p => ({...p, name: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Email *" value={workerForm.email} onChange={e => setWorkerForm(p => ({...p, email: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Password *" type="password" value={workerForm.password} onChange={e => setWorkerForm(p => ({...p, password: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Phone" value={workerForm.phone} onChange={e => setWorkerForm(p => ({...p, phone: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder="Address" value={workerForm.address} onChange={e => setWorkerForm(p => ({...p, address: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <select value={workerForm.service_id} onChange={e => setWorkerForm(p => ({...p, service_id: e.target.value}))} className="w-full rounded-lg md:rounded-sm border border-[#E2E0D9] bg-white h-12 md:h-10 px-3 text-sm outline-none focus:ring-2 focus:ring-[#1C4532]/20">
                <option value="">Select service</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input type="number" placeholder="Price" value={workerForm.price} onChange={e => setWorkerForm(p => ({...p, price: e.target.value}))} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F3F1ED] cursor-pointer">
                <input type="checkbox" checked={workerForm.verified} onChange={e => setWorkerForm(p => ({...p, verified: e.target.checked}))} className="accent-[#1C4532] w-4 h-4" />
                <span className="text-sm font-medium text-[#1A1D1A]">Mark as verified (skip verification)</span>
              </label>
            </div>
            <DialogFooter>
              <Button onClick={createWorker} disabled={savingWorker} className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white w-full md:w-auto">{savingWorker ? "Creating..." : "Create Worker"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="md:rounded-sm rounded-xl">
            <DialogHeader><DialogTitle className="font-display">{editingService ? t("admin.dialog.editService") : t("admin.dialog.newService")}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <Input placeholder={t("admin.serviceForm.name")} value={serviceForm.name} onChange={e => setServiceForm({...serviceForm, name: e.target.value})} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Textarea placeholder={t("admin.serviceForm.description")} value={serviceForm.description} onChange={e => setServiceForm({...serviceForm, description: e.target.value})} className="md:rounded-sm rounded-lg"/>
              <Input placeholder={t("admin.serviceForm.category")} value={serviceForm.category} onChange={e => setServiceForm({...serviceForm, category: e.target.value})} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input type="number" placeholder={t("admin.serviceForm.basePrice")} value={serviceForm.base_price} onChange={e => setServiceForm({...serviceForm, base_price: e.target.value})} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder={t("admin.serviceForm.imageUrl")} value={serviceForm.image_url} onChange={e => setServiceForm({...serviceForm, image_url: e.target.value})} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
              <Input placeholder={t("admin.serviceForm.icon")} value={serviceForm.icon} onChange={e => setServiceForm({...serviceForm, icon: e.target.value})} className="md:rounded-sm rounded-lg h-12 md:h-10"/>
            </div>
            <DialogFooter>
              <Button onClick={saveService} data-testid="save-service" className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white w-full md:w-auto">{t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      {/* Mobile FAB for admin actions */}
      <div className="fixed bottom-6 right-6 z-40 md:hidden">
        <button
          onClick={() => openServiceDialog(null)}
          className="grid h-14 w-14 place-items-center rounded-full bg-[#1C4532] text-white shadow-[0_8px_25px_rgba(28,69,50,0.35)] active:scale-90 transition-transform"
        >
          <Plus size={24} />
        </button>
      </div>
    </div>
    </>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="mobile-card md:premium-surface p-4 md:p-5 transition-all haptic-tap">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg md:rounded-sm bg-[#F3F1ED] text-[#1C4532]">{icon}</div>
      <p className="font-display text-2xl md:text-3xl mt-3 text-[#1A1D1A]">{value}</p>
      <p className="text-[10px] md:text-xs uppercase tracking-wider text-[#646964] mt-1">{label}</p>
    </div>
  );
}
